<script lang="ts" setup>
import IconRatingStar from '~theme/components/atoms/icons/IconRatingStar.vue';

const DEFAULT_STEP = 0.1;
const DEFAULT_MAX = 5;

const props = withDefaults(
  defineProps<{
    max?: number;
    step?: number;
    rate?: number;
    showRateNumber?: boolean;
  }>(),
  {
    max: DEFAULT_MAX,
    step: DEFAULT_STEP,
    rate: 0,
    showRateNumber: false,
  }
);

const flooredMax = computed((): number => Math.floor(props.max));

const normalizedRate = computed((): number => {
  if (props.rate < 0) {
    return 0;
  }
  return Math.min(props.rate, flooredMax.value);
});

const rateString = computed((): string => {
  return normalizedRate.value.toFixed(1);
});

const normalizedStep = computed((): number => {
  if (props.rate < 0) {
    return DEFAULT_STEP;
  }
  if (props.rate > 1) {
    return 1;
  }
  return props.rate;
});

const starRates = computed((): number[] => {
  const { integer, fraction } = splitNumberParts(
    normalizedRate.value,
    normalizedStep.value
  );
  return Array.from({ length: flooredMax.value }).map((_, index) => {
    if (integer === index) {
      return fraction;
    }
    return integer > index ? 1 : 0;
  });
});
</script>

<template>
  <div :class="$style.container">
    <ul :class="$style.list">
      <li
        v-for="(starRate, index) in starRates"
        :key="index"
        :class="$style.list__item"
      >
        <IconRatingStar :rate="starRate" :class="$style.icon" />
      </li>
    </ul>
    <p v-if="showRateNumber" :class="$style['rate-number']">{{ rateString }}</p>
  </div>
</template>

<style lang="scss" module>
.container {
  display: flex;
  align-items: center;
}

.list {
  display: flex;
  align-items: center;
  padding: 0px;

  &__item {
    padding: 0.2em;
    line-height: 1;
  }
}

.icon {
  width: 1em;
  height: 1em;
}

.rate-number {
  color: $color-text-and-object-accent-yellow;
  font-weight: bold;
  margin-left: 10px;
}
</style>
